import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const ResumeManager = ({ onLoadResume, onBack }) => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/resumes`);
      setResumes(response.data);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      setError('Failed to load resumes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResume = async (resumeId) => {
    if (window.confirm('Are you sure you want to delete this resume?')) {
      try {
        await axios.delete(`${API_BASE_URL}/resumes/${resumeId}`);
        fetchResumes(); // Refresh the list
      } catch (error) {
        console.error('Error deleting resume:', error);
        alert('Error deleting resume. Please try again.');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredResumes = resumes.filter(resume =>
    resume.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resume.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleLoadResume = async (resume) => {
    try {
      // Fetch the full resume with all details
      const response = await axios.get(`${API_BASE_URL}/resumes/${resume.id}`);
      const fullResume = response.data;
      onLoadResume(fullResume);
    } catch (error) {
      console.error('Error loading resume:', error);
      alert('Error loading resume. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="resume-preview">
        <div className="container">
          <div className="card">
            <div className="loading-overlay">
              <div className="loading-modal">
                <div className="spinner"></div>
                <span>Loading resumes...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="resume-preview">
      <div className="container">
        {/* Header */}
        <div className="card">
          <div className="preview-header">
            <div>
              <h1>Resume Manager</h1>
              <p>View and manage your saved resumes</p>
            </div>
            <div className="preview-buttons">
              <button
                onClick={onBack}
                className="btn btn-secondary"
              >
                ← Back to Builder
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="card">
          <div className="form-group">
            <label className="form-label">Search Resumes</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field"
              placeholder="Search by name or email..."
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="card">
            <div className="error-message">
              {error}
            </div>
          </div>
        )}

        {/* Resumes List */}
        <div className="card">
          <h2 className="section-title">Saved Resumes ({filteredResumes.length})</h2>
          
          {filteredResumes.length === 0 ? (
            <div className="no-projects">
              {searchTerm ? 'No resumes found matching your search.' : 'No resumes saved yet.'}
            </div>
          ) : (
            <div className="projects-grid">
              {filteredResumes.map(resume => (
                <div key={resume.id} className="project-card">
                  <div className="project-card-content">
                    <div className="project-info">
                      <h3 className="project-name">{resume.name}</h3>
                      <div className="project-meta">
                        <span className="project-category">{resume.email}</span>
                        <span className="project-difficulty">{resume.mobile}</span>
                      </div>
                      <div className="project-description">
                        <strong>Created:</strong> {formatDate(resume.createdAt)}
                      </div>
                      {resume.updatedAt && resume.updatedAt !== resume.createdAt && (
                        <div className="project-description">
                          <strong>Updated:</strong> {formatDate(resume.updatedAt)}
                        </div>
                      )}
                      <div className="project-description">
                        <strong>Contact:</strong> {resume.email} | {resume.mobile}
                      </div>
                    </div>
                    <div className="project-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                      <button
                        type="button"
                        onClick={() => handleLoadResume(resume)}
                        className="btn btn-primary"
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                      >
                        Load Resume
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteResume(resume.id)}
                        className="btn btn-danger"
                        style={{ fontSize: '0.8rem', padding: '0.3rem 0.6rem' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumeManager; 